import React, { useState } from 'react';

import { Schedule } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { isFuture } from 'date-fns';
import { Link } from 'react-router-dom';
import { FavoriteRowButton } from 'src/components/FavoriteRowButton';
import { ConvocationSummary } from 'src/types/convocation-summary';
import { Favorite } from 'src/types/favorite';
import { formatDate } from 'src/utils/date';

import { SummaryCard } from '../SummaryCard';

interface TimelineMobileViewProps {
  data: ConvocationSummary[];
  isLoading: boolean;
  isError: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination: React.Dispatch<
    React.SetStateAction<{
      pageIndex: number;
      pageSize: number;
    }>
  >;
  totalCount: number;
  handleAddFavorite: (rowId: number, favorite: Favorite) => void;
  handleRemoveFavorite: (rowId: number) => void;
}

export function TimelineMobileView({
  data,
  isLoading,
  isError,
  pagination,
  setPagination,
  totalCount,
  handleAddFavorite,
  handleRemoveFavorite,
}: TimelineMobileViewProps) {
  const [openRow, setOpenRow] = useState<number | null>(null);

  const totalPages = Math.ceil(totalCount / pagination.pageSize);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPage - 1,
    }));
    setOpenRow(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Erro ao carregar dados
      </Alert>
    );
  }

  return (
    <Box>
      {data.map((row) => (
        <Card key={row.id} sx={{ mb: 2 }} elevation={1}>
          <CardContent sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => setOpenRow(openRow === row.id ? null : row.id)}
              sx={{ cursor: 'pointer' }}>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom>
                    {formatDate(row.datareferencia)}
                  </Typography>
                  {isFuture(new Date(row.datareferencia)) && (
                    <Tooltip title="Evento futuro" placement="top">
                      <Schedule sx={{ mb: 1 }} color="primary" />
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight="bold"
                  color="primary.main"
                  display="flex"
                  alignItems="center"
                  gap={1}>
                  <FavoriteRowButton
                    type="operation"
                    rowId={Number(row.operation.hashId)}
                    favorite={row.favorite as Favorite | undefined}
                    handleAddFavorite={handleAddFavorite}
                    handleRemoveFavorite={handleRemoveFavorite}
                  />
                  <Link
                    style={{
                      color: 'inherit',
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                    to={`/operacao/${row.market.slug}/${row.operation.slug}/${row.operation.hashId}`}>
                    {row.operation.name}
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {row.categoriadocumento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {row.especiedocumento}
                </Typography>
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {row.market.name}
                </Typography>
              </Box>
              {openRow === row.id ? (
                <KeyboardArrowUpIcon color="action" />
              ) : (
                <KeyboardArrowDownIcon color="action" />
              )}
            </Box>
            <Collapse in={openRow === row.id} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              <SummaryCard convocation={row} />
            </Collapse>
          </CardContent>
        </Card>
      ))}

      {data.length === 0 && !isLoading && (
        <Card>
          <CardContent>
            <Box
              component="div"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: '#F5F5F5',
                py: 4,
              }}>
              <Typography>Nenhum resultado encontrado</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
          }}>
          <Pagination
            count={totalPages}
            page={pagination.pageIndex + 1}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
}
